import { useQuery } from '@apollo/client';
import {
  Button,
  Distribute,
  Layout,
  Spacer,
  SvgUsersAdd,
  Text,
  TextButton,
  Tooltip,
} from '@loomhq/lens';
import cn from 'classnames';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import {
  INVITE_MEMBERS_WORKSPACE_SELECTOR_BUTTON_CLICKED,
  INVITE_TEAMMATES_CLICKED,
} from 'constants/events';
import {
  MULTIPLE_WORKSPACES,
  ROLLOUT_WORKSPACE_CONSOLIDATE_EXISTING_USERS_SELECTOR,
} from 'constants/featureFlag';
import {
  CREATE_NEW_WORKSPACE_MODAL,
  TEAM_INVITE_MODAL,
  WORKSPACE_SELECTOR_MODAL,
} from 'constants/modalTypes';
import {
  INVITE_ADMIN_ACTION,
  INVITE_CREATOR_ACTION,
  INVITE_CREATOR_LITE_ACTION,
  INVITE_VIEWER_ACTION,
  SUGGESTED_WORKSPACE_WORKSPACE_SWITCHER,
} from 'constants/scopes';
import { selectCurrentUser } from 'selectors/user';
import * as analytics from 'utilities/analytics';
import { useSeenNewSuggestedWorkspaceWorkspaceSwitcher } from 'utilities/localStorage/suggestedWorkspace';

import useClickOutside from 'hooks/useClickOutside';
import { useFlagIsActivated } from 'hooks/featureFlag';
import { useHasSomeScopes } from 'hooks/useHasScopes';
import { useGetSelectedWorkspace } from 'hooks/workspace';

import PendingInvites from './pendingInvites';
import SuggestedWorkspace from './suggested-workspace';
import WorkspaceList from './workspaceList';
import DropdownHeader from '../../navigation/dropdown-header';
import { APP_SOURCE_MAP } from '../../../../../../shared/constants/analytics';
import { ANY } from '../../../../../../shared/constants/analyticsInviteTypes';
import { WORKSPACE_SELECTOR } from '../../../../../../shared/constants/analyticsSources';
import { INVITE_STEP_COMPLETE } from '../../../../../../shared/constants/events';

import Scopes from 'components/scopes';
import GetSuggestedWorkspaceForBanner from 'components/share-video/join-team-banner/GetSuggestedWorkspaceForBanner';
import WorkspaceLogo from 'components/workspace-logo';

import styles from './styles.module.less';

const WorkspaceSelector = ({ isCompact = false }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const workspaces = useSelector(state => state.workspace.workspaces);
  const [organization] = workspaces;
  const closeWorkspaceSelector = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const ref = useClickOutside(closeWorkspaceSelector);
  const showCreateWorkspaceModal = useFlagIsActivated({
    flag: MULTIPLE_WORKSPACES,
    activationValues: [true],
  });
  const [suggestedWorkspaceTextDismissed, dismissNewSuggestedWorkspaceText] =
    useSeenNewSuggestedWorkspaceWorkspaceSwitcher();
  const hasInviteScope = useHasSomeScopes([
    INVITE_VIEWER_ACTION,
    INVITE_CREATOR_ACTION,
    INVITE_CREATOR_LITE_ACTION,
    INVITE_ADMIN_ACTION,
  ]);
  const hasSuggestedWorkspaceScope = useHasSomeScopes([
    SUGGESTED_WORKSPACE_WORKSPACE_SWITCHER,
  ]);
  const rolloutConsolidatedWorkspaces = useFlagIsActivated({
    flag: ROLLOUT_WORKSPACE_CONSOLIDATE_EXISTING_USERS_SELECTOR,
    activationValues: [true],
  });
  const { data: suggestedWorkspaceData } = useQuery(
    GetSuggestedWorkspaceForBanner,
    {
      fetchPolicy: 'cache-first',
    }
  );
  const suggestedWorkspaceUnavailable =
    !suggestedWorkspaceData?.result ||
    suggestedWorkspaceData.result?.isCurrentUserMember;
  const showSuggestedWorkspace =
    hasSuggestedWorkspaceScope &&
    rolloutConsolidatedWorkspaces &&
    !suggestedWorkspaceUnavailable;
  const showSuggestedWorkspaceText =
    showSuggestedWorkspace && !suggestedWorkspaceTextDismissed;
  const selectedWorkspace = useGetSelectedWorkspace();

  const toggleDropdown = () => {
    if (!open) {
      setOpen(true);
      dismissNewSuggestedWorkspaceText();
    } else {
      setOpen(false);
    }
  };

  if (!organization) {
    return null;
  }

  return (
    <>
      <div
        ref={ref}
        className={cn(
          styles.dropdownWrapper,
          hasInviteScope && styles.withInviteButton
        )}
      >
        <DropdownHeader
          toggleDropdown={toggleDropdown}
          selectedWorkspace={selectedWorkspace}
          isCompact={isCompact}
          showSuggestedWorkspaceText={showSuggestedWorkspaceText}
        />

        {open && (
          <div className={styles.dropdownCard}>
            <div className={styles.currentWorkspaceSection}>
              <div className={styles.currentWorkspaceAvatarSection}>
                <WorkspaceLogo workspace={selectedWorkspace} size="large" />
              </div>
              <Spacer top="small">
                <Text size="large" fontWeight="bold" hasEllipsis>
                  {selectedWorkspace.name}
                </Text>
                <Text color="bodyDimmed">
                  {selectedWorkspace.members}
                  {selectedWorkspace.members == 1 ? ' member' : ' members'}
                </Text>
              </Spacer>
            </div>

            {showSuggestedWorkspace && (
              <SuggestedWorkspace
                suggestedWorkspaceData={suggestedWorkspaceData}
              />
            )}

            <WorkspaceList selectedWorkspace={selectedWorkspace} />

            <PendingInvites />

            <Distribute
              className={styles.workspacesActionsSection}
              gap="small"
              direction="column"
            >
              <Scopes
                names={[
                  INVITE_VIEWER_ACTION,
                  INVITE_CREATOR_ACTION,
                  INVITE_CREATOR_LITE_ACTION,
                  INVITE_ADMIN_ACTION,
                ]}
                some
              >
                <Button
                  hasFullWidth
                  variant="primary"
                  onClick={() => {
                    dispatch(openModal(TEAM_INVITE_MODAL));

                    analytics.track(
                      INVITE_MEMBERS_WORKSPACE_SELECTOR_BUTTON_CLICKED,
                      {
                        path: window.location.pathname,
                      }
                    );

                    analytics.track(INVITE_STEP_COMPLETE, {
                      step: INVITE_TEAMMATES_CLICKED,
                      device: APP_SOURCE_MAP.WEB,
                      source: WORKSPACE_SELECTOR,
                      inviteType: ANY,
                      path: window.location.pathname,
                    });
                  }}
                  icon={<SvgUsersAdd />}
                >
                  Invite teammates
                </Button>
              </Scopes>
              {workspaces.length > 1 && (
                <div className="width:full">
                  <Button
                    hasFullWidth
                    onClick={() =>
                      dispatch(openModal(WORKSPACE_SELECTOR_MODAL))
                    }
                  >
                    Change default Workspace
                  </Button>
                </div>
              )}
              {showCreateWorkspaceModal && (
                <Layout
                  className="text:center width:full"
                  alignment="center"
                  gap={4.5}
                >
                  <Tooltip
                    content={
                      currentUser.deletion_pending
                        ? 'This account will be deleted. Undo to create a Workspace'
                        : null
                    }
                  >
                    <TextButton
                      className="width:full flex justify:center grow:1"
                      isDisabled={currentUser.deletion_pending}
                      onClick={() =>
                        dispatch(openModal(CREATE_NEW_WORKSPACE_MODAL))
                      }
                    >
                      Create new Workspace
                    </TextButton>
                  </Tooltip>
                </Layout>
              )}
            </Distribute>
          </div>
        )}
      </div>
    </>
  );
};

export default WorkspaceSelector;
